import spartaEventsUtility from "utilities/global/sparta-events-utility/2.3.5/js/sparta-events-utility";
import spartaGlobalFunctions from "utilities/global/sparta-global-functions-utility/2.1.3/js/sparta-global-functions-utility";
import spartaStoreUtility from "utilities/global/sparta-store-utility/3.1.1/js/sparta-store-utility";
import jQuery from "utilities/vendor/jquery/3.5.1/js/jquery";
import $ from "utilities/vendor/jquery/3.5.1/js/jquery";
import spartaValidationFramework from "utilities/global/sparta-validation-framework/7.1.6/js/sparta-validation-framework";
/* global spartaEventsUtility spartaValidationFramework bactm_captureCustomError */
/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * @name        Sparta Input Error Utility
 * @file        sparta-input-error-utility.js
 * @description Provides standard error messaging for Sparta inputs
 * @classdesc   Provides standard error messaging for Sparta inputs
 * @global      Creates a singleton of the global spartaInputErrorUtility class
 * @author      Kerry Boyce
 * @copyright   © 2016 Bank of America Corporation. All rights reserved.
 *
 * The Sparta Input Error Utility is used to show and hide the standard Sparta inline error
 * messaging experience, as designed by the global UX Standards Team.  When included, it will
 * automatically integrate with the Sparta Validation Framework, be subscribing to its validation
 * events, but the "updateInlineError" method may also be called directly, if needed.
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

// default settings for ui layer class
const classDefaults = {
  initNamespace: 'sparta-input-error-utility',
  globalReady: 'SPARTA_GLOBAL_READY.spartaBootstrapUtility',
};

// empty variable to store a reference to a current instance of the utility
let instance;

/**
 * @class spartaInputErrorUtility
 * @classdesc Class for global implementation of the standard Sparta 2.0 inline error messaging
 * experience
 */
class SpartaInputErrorUtility {
  /**
   * @method
   * @name constructor
   * @desc Initialize an instance of the inline error messaging utility class
   * @param {Object} options - object of various options to override the classDefaults above
   */
  constructor(options) {
    // extend class options based on defaults and provided constructor options
    if (options) {
      this.options = Object.assign({}, classDefaults, options);
    }

    // make sure we've got a singleton using our instance variable from above
    if (!instance) {
      const errorsObject = {};

      const errorPromises = [];

      instance = this;

      this.options.$document.one(`${this.options.globalReady}.${this.options.initNamespace}`,
        this._addListeners()).bind(this);

      Object.assign(instance, { errorsObject, errorPromises });
    }

    return instance;
  }

  /**
   * @method
   * @name _addListeners
   * @desc subscribes to the Validation Framework Sparta events for validation failures and
   * successes
   */
  _addListeners() {
    /* subscribe to the Sparta Validation Framework event for failed inline validations */
    spartaEventsUtility.sub(spartaValidationFramework.EVENTS.VALIDATION_FAILURE_EVENT,
      (data) => {
        const { data: input } = data;

        const { rule } = input;

        const errorObject = {
          [rule]: {
            valid: false,
            input,
          },
        };

        instance.errorsObject[input.field[0].id] = Object.assign({}, instance.errorsObject[input.field[0].id], errorObject);

        instance.errorPromises.push(instance.updateInlineError(true, data.data));

        instance.triggerCoremetricsEvent(data.data.rule, data.data.message);
      }
    );

    /* subscribe to the Sparta Validation Framework event for successful inline validations */
    spartaEventsUtility.sub(spartaValidationFramework.EVENTS.VALIDATION_SUCCESS_EVENT, (data) => {
      const { data: input } = data;

      const { rule } = input;

      const errorObject = {
        [rule]: {
          valid: true,
        },
      };

      instance.errorsObject[input.field[0].id] = Object.assign({}, instance.errorsObject[input.field[0].id], errorObject);

      instance.errorPromises.push(instance.updateInlineError(false, data.data));
    });

    /* subscribe to the Sparta Validation Framework event for failed required field checks */
    spartaEventsUtility.sub(spartaValidationFramework.EVENTS.REQUIRED_FIELD_FAILURE_EVENT,
      (data) => {
        const { data: input } = data;

        const { rule } = input;

        const errorObject = {
          [rule]: {
            valid: false,
            input: input,
          },
        };

        instance.errorsObject[input.field[0].id] = Object.assign({}, instance.errorsObject[input.field[0].id], errorObject);

        instance.errorPromises.push(instance.updateInlineError(true, data.data));

        instance.triggerCoremetricsEvent(data.data.rule, data.data.message);

        data.data.field.on('change.requiredRecheck', () => {
          data.data.field.off('change.requiredRecheck');

          const iRecheckDelay = setTimeout(() => {
            spartaValidationFramework.fn.core.validateRequiredFields(data.data.field);
          }, 50);
        });
      }
    );

    /* subscribe to the Sparta Validation Framework event for successful required field checks */
    spartaEventsUtility.sub(spartaValidationFramework.EVENTS.REQUIRED_FIELD_SUCCESS_EVENT,
      (data) => {
        const { data: input } = data;

        const { rule } = input;

        const errorObject = {
          [rule]: {
            valid: true,
          },
        };

        instance.errorsObject[input.field[0].id] = Object.assign({}, instance.errorsObject[input.field[0].id], errorObject);

        instance.errorPromises.push(instance.updateInlineError(false, data.data));
      }
    );
  }

  /**
   * @method
   * @name updateInlineError
   * @desc Updates an input and its label to either show or hide input errors.
   * @param {Boolean} bShowError - whether the error should be shown ('false' will hide it)
   * @param {Object} oErrorData - Collection of information about the error that was triggered in
   *                              the format:
   *                                  {jQuery Collection} field - the input that was validated
   *                                  {String} rule - the validation rule that was run
   *                                  {String} message - the error message to be shown (blank on
   *                                    success)
   *                                  {jQuery Collection} label - the label of input that was
   *                                    validated
   * @example spartaInputErrorUtility.updateInlineError(true, oErrorData);
   */
  updateInlineError(bShowError, oErrorData) {
    return new Promise((resolve) => {
      // Determine the target error message container for the field */
      const sErrorMessageContainderId = `${oErrorData.field.attr('id')}_errorMessage`;
      let inputCurrentAriaDescribedBy = oErrorData.field.attr('aria-describedby');
      inputCurrentAriaDescribedBy = inputCurrentAriaDescribedBy ? inputCurrentAriaDescribedBy.replace(sErrorMessageContainderId, '') : '';
      let sErrorMessageContainderValue = inputCurrentAriaDescribedBy + ' ' + sErrorMessageContainderId;
      const $errorMessageContainer = $(`#${sErrorMessageContainderId}`);

      // re-order input aria described by so invalid text is read in proper order
      let sErrorMessageContainderValueArray = sErrorMessageContainderValue.split(' '); // convert string to array
      sErrorMessageContainderValueArray.splice(1, 0, sErrorMessageContainderValueArray.pop()) // shift error to [1] index
      let sErrorMessageContainderValueString = sErrorMessageContainderValueArray.join(' ').replace(/\s{2,}/g,' ').trim(); // convert array back to string

      // update dom classes
      const sInputValidationErrorClass = spartaValidationFramework.data.globals.INPUT_ERROR_BASE_CLASS +
        spartaValidationFramework.data.globals.VALIDATION_ERROR_SUFFIX;
      const sRequiredFieldErrorClass = spartaValidationFramework.data.globals.INPUT_ERROR_BASE_CLASS +
        spartaValidationFramework.data.globals.REQUIRED_ERROR_SUFFIX;
      const iUpdateDelayLength = (instance.isNavigationElement()) ? 200 : 0;
      const bIsRequiredFieldUpdate = (oErrorData.rule === 'required-field');

      const iUpdateDelay = setTimeout(() => {
        if (bShowError) {
          $errorMessageContainer.html(oErrorData.message);
          $errorMessageContainer.attr('aria-hidden', false);
          oErrorData.field.attr({ 'aria-describedby': sErrorMessageContainderValueString, 'aria-invalid': true });

          if (!bIsRequiredFieldUpdate) {
            $(`#${oErrorData.label.attr('for')}`).focus();
          }
        } else {
          if (!(oErrorData.field.hasClass(sInputValidationErrorClass) || oErrorData.field.hasClass(sRequiredFieldErrorClass))) {
            oErrorData.field.removeAttr('aria-invalid');
            oErrorData.field.attr('aria-describedby', inputCurrentAriaDescribedBy.replace(/\s{2,}/g,' ').trim());
            $errorMessageContainer.attr('aria-hidden', true);
            $errorMessageContainer.empty();
          }
        }

        if (oErrorData.field.hasClass('gis-mask')) {
          const sErrorClass = (bIsRequiredFieldUpdate) ? sRequiredFieldErrorClass : sInputValidationErrorClass;
          const $maskedSiblingInput = oErrorData.field.siblings('.gis-mask-value');

          $maskedSiblingInput.toggleClass(sErrorClass, bShowError);

          if (bShowError) {
            $maskedSiblingInput.attr({ 'aria-describedby': inputCurrentAriaDescribedBy.replace(/\s{2,}/g,' ').trim(), 'aria-invalid': true });
          } else {
            $maskedSiblingInput.removeAttr('aria-invalid');
          }
        }
        return resolve(true);
      }, iUpdateDelayLength);
    });
  }

  /**
   * @method
   * @name isNavigationElement
   * @desc Determines if the currently active page element is a navigation-type element.
   * @return {Boolean} - indicating if the currently active page element is a link, buttton, or "button-type" input
   * @example let bIsNavigationElement = spartaInputErrorUtility.isNavigationElement();
   */
  isNavigationElement() {
    const nHoveredElements = document.querySelectorAll(':hover');

    if (nHoveredElements.length > 0) {
      const nCurrentElement = nHoveredElements[(nHoveredElements.length - 1)];
      const sCurrentElementType = nCurrentElement.nodeName.toLowerCase();

      return ((sCurrentElementType === 'a') ||
        (sCurrentElementType === 'button') ||
        ((sCurrentElementType === 'input') && (nCurrentElement.type === 'button')) ||
        ($(nCurrentElement).closest('a, button, input[type="button"]').length > 0));
    } else {
      return false;
    }
  }

  /**
   * @method
   * @name triggerCoremetricsEvent
   * @desc Determines if the currently active page element is a navigation-type element.
   * @param {string} sFailedRule - the failed validation rule name (must be uniaue, per input))
   * @param {string} sDisplayedMessage - the error message that was displayed to the user
   * @example spartaInputErrorUtility.triggerCoremetricsEvent("required", "This is a required
   * field.");
   */
  triggerCoremetricsEvent(sFailedRule, sDisplayedMessage) {
    /* Trigger Coremetrics error event, if Coremetrics is avalilable on the page */
    if (typeof bactm_captureCustomError === 'function') {
      bactm_captureCustomError(sFailedRule, sDisplayedMessage);
    }
  }

  /**
   * @method
   * @name collectAndDisplayErrrors
   * @desc Accepts a form ID and a Sparta Messaging Utility instance and displays
   * relevant form errors for the user
   * @param {String} formID string representation of the form's ID attribute
   * @param {Object} errorMessage initialized instance of the spartaMessagingUtility
   * @param {Boolean || Object} [focusErrorMessage = true] should error message receive focus. If given an object options included to control how focus set set
   * @param {String} [errorMessageTitle] String to edit the output title of message utility. If string includes [errorMessages.length] this gets replaced with number of errors
   * @memberof SpartaInputErrorUtility
   */
  collectAndDisplayErrrors(formID, errorMessage, focusErrorMessage = true, errorMessageTitle) {


    // if we don't have a form and an error message instance, we cannot proceed
    if (formID && errorMessage) {
      return new Promise((resolve) => {
        // get form from page
        const form = document.getElementById(formID);
        if (form) {
          // get promises created to update error messages from instance
          const { errorPromises } = instance;

          // make sure all error messages are done being updated
          const test = Promise.all(errorPromises).then(() => {

            // get object of errors from instance
            let { errorsObject } = instance;

            // initialize array for storing actual error message strings
            // we need this because we want to pass an array to the Messaging Utility
            // so that it generates a <ul>
            let errorMessages = [];

            // find form inputs that are invalid and not hidden
            const invalidInputs = Array.from(form.querySelectorAll('input[aria-invalid]:not([aria-hidden="true"])'));

            // get array of Error Object keys so we can iterate and get # of errors
            // ensure our array is building ONLY targeted inputs
            // prevents messages from building up over multiple forms on same page
            const errorKeysArray = [];
            invalidInputs.forEach( (thisInput) => {
              const inputID = thisInput.getAttribute('id');
              errorKeysArray.push(inputID)
            });

            // generate Array of error messages to send to Messaging Utility
            errorMessages = errorKeysArray
              // filter out valid fields
              .filter(item => !Object
                .keys(errorsObject[item])
                .every(validation => errorsObject[item][validation].valid)
              )

              // sort fields based on DOM source order
              .sort((a, b) => {
                // we're going to need to sort based on DOM source order
                // so we'll use some temporary variables for the actual A and B we compare
                let indexA = 0;
                let indexB = 0;

                // iterate through our invalid inputs looking for element with ID == A
                invalidInputs.some((item, index) => {
                  // when we find the element with our ID
                  if (item.id === a) {
                    // store the element's index in our NodeList turned Array
                    indexA = index;

                    // exit iteration
                    return true;
                  }

                  return false;
                });

                // iterate through our invalid inputs looking for element with ID == B
                invalidInputs.some((item, index) => {
                  // when we find the element with our ID
                  if (item.id === b) {
                    // store the element's index in our NodeList turned Array
                    indexB = index;

                    // exit iteration
                    return true;
                  }

                  return false;
                });

                // sort based on our discovered indexes in the NodeList turned Array
                return indexA - indexB;
              })

              // map through our sorted Array and generate Array of error messages for display
              .map((item) => {
                // find an error for this input by iterating through the available validation keys
                const validationErrorKey = Object.keys(errorsObject[item]).find((validation) => {
                  // if we have a validation that is not valid
                  if (!errorsObject[item][validation].valid) {
                    // return this item
                    return true;
                  }
                });

                // get the validation object for this input
                const validationError = errorsObject[item][validationErrorKey].input;

                // clone the label so we can get the text from it
                // we deeply clone because these label elements can have several children
                const label = validationError.label[0].cloneNode(true);

                // get the required indicator container element if this label has one
                const requiredIndicator = label.querySelector('.spa-input-label--required');

                // get the ADA text container element if this label has one
                const adaLabel = label.querySelector('.ada-hidden');

                // remove the required indicator element
                if (requiredIndicator) requiredIndicator.parentNode.removeChild(requiredIndicator);

                // remove the ADA text element
                if (adaLabel) adaLabel.parentNode.removeChild(adaLabel);

                // return formatted error string prepended with field label
                return `<strong>${label.innerText.trim()}</strong>: ${validationError.message.trim()}`;
              })
            ; // END errorMessages setter

            // destroy the error message if it existed previously
            errorMessage.destroy();

            if (errorMessages.length) {

              // get message from JCR attached to form as data attribute
              if (!errorMessageTitle && form.getAttribute('data-validation-errors-title')) {
                errorMessageTitle = form.getAttribute('data-validation-errors-title');
              }
              // get language from global params
              else if (!errorMessageTitle) {
                // get language substring from stored page lang, example 'en-us', and params expects 'en'
                const pageLanguage = spartaStoreUtility ? spartaStoreUtility.getData('lang').substring(0,2) : null;
  
                // get validation errors object from global params based on page language
                const pageLanguageObj = global.spaParams.global[pageLanguage].validationErrors ? global.spaParams.global[pageLanguage].validationErrors : null;
                errorMessageTitle = pageLanguageObj && errorMessages.length ? pageLanguageObj.validationErrorTitle : ``;
              };

              // if msg title contains error msg length, update it with JS value
              // if not set from JCR or global param than we're getting it from function call
              errorMessageTitle = errorMessageTitle.replace('[errorMessages.length]', errorMessages.length);

              // generate new error message and prepend it inside the form
              errorMessage
                .type('error')
                .title(errorMessageTitle)
                .message(errorMessages)
                .prepend(`#${formID}`)
              ;

              // check to see if we need to focus on the error message
              if (focusErrorMessage === true) {
                // trigger focus on error message element
                // use delay as message container may not have been created yet
                setTimeout(()=> {
                  document.getElementById(`sparta-message-${errorMessage._options.uuid}`).focus();
                }, 100);
              }
              // check to see if the focus is an object with options
              else if (focusErrorMessage && typeof focusErrorMessage === 'object') {
                setTimeout(()=> {
                  // get default target
                  let target = document.getElementById(`sparta-message-${errorMessage._options.uuid}`);
                  let errorList = target.querySelector('.spartaMessage-list');
                  
                  // if custom target, get that
                  if (focusErrorMessage.focusTarget) {
                    target = target.querySelector(focusErrorMessage.focusTarget) || target;
                  }

                  // ensure target has a tab index if target has tabindex so it's focusable by JS
                  target.setAttribute('tabindex', '-1');

                  // add aria label to target if given
                  if (focusErrorMessage.adaLabel) {
                    target.setAttribute('aria-label', focusErrorMessage.adaLabel.replace('[errorMessages.length]', errorMessages.length));
                  }

                  // check if error messages need to be hidden from view
                  if (focusErrorMessage.hideErrorList) {
                    errorList.style.display = 'none';
                  }

                  // set focus to target
                  target.focus();                
                }, 100);
              }

              return resolve(false);

            } // END errorMessages.length

            return resolve(true);
          });
        }
      });
    }

    return false;
  }
}

// options specific to the call we'll make to the constructor below
const options = {
  $window: $(window),
  $document: $(document),
};

const spartaInputErrorUtility = new SpartaInputErrorUtility(options);

// export new instance of the inline error messaging utility
export default spartaInputErrorUtility;
